import Phaser from "phaser";
import { COLORS, hexToPhaser } from "../colors";

// ------------- Global console interceptor (runs at module load time) -------------

const logBuffer: string[] = [];
const MAX_LOG_LINES = 1000;

const _origLog = console.log.bind(console);
const _origWarn = console.warn.bind(console);
const _origError = console.error.bind(console);

function serialize(args: any[]): string {
  return args
    .map((a) => {
      if (typeof a === "object" && a !== null) {
        try {
          return JSON.stringify(a);
        } catch {
          return String(a);
        }
      }
      return String(a);
    })
    .join(" ");
}

console.log = (...args: any[]) => {
  _origLog(...args);
  logBuffer.push("[LOG] " + serialize(args));
  if (logBuffer.length > MAX_LOG_LINES) logBuffer.shift();
};

console.warn = (...args: any[]) => {
  _origWarn(...args);
  logBuffer.push("[WARN] " + serialize(args));
  if (logBuffer.length > MAX_LOG_LINES) logBuffer.shift();
};

console.error = (...args: any[]) => {
  _origError(...args);
  logBuffer.push("[ERR] " + serialize(args));
  if (logBuffer.length > MAX_LOG_LINES) logBuffer.shift();
};

// ---------------------------------------------------------------------------------

export class DebugWindow {
  private scene: Phaser.Scene;
  private uiLayer: Phaser.GameObjects.Container;

  private container: Phaser.GameObjects.Container | null = null;
  private textObj: Phaser.GameObjects.Text | null = null;
  private maskGraphics: Phaser.GameObjects.Graphics | null = null;

  private scrollY: number = 0;
  private isDragging: boolean = false;
  private lastPointerY: number = 0;

  // Named references so we can remove exact listeners later
  private onPointerMove: ((ptr: Phaser.Input.Pointer) => void) | null = null;
  private onPointerUp: (() => void) | null = null;

  constructor(scene: Phaser.Scene, uiLayer: Phaser.GameObjects.Container) {
    this.scene = scene;
    this.uiLayer = uiLayer;
  }

  show() {
    this.hide();

    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const pad = 12;
    const headerH = 48;
    const textAreaTop = headerH;
    const textAreaH = H - headerH;

    // Full-screen dark background – blocks all input below
    const bg = this.scene.add
      .rectangle(0, 0, W, H, 0x000000, 0.95)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setInteractive();

    // Title
    const title = this.scene.add
      .text(pad, pad, "Debug Console", {
        fontSize: "16px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
      })
      .setOrigin(0, 0)
      .setScrollFactor(0);

    // Close button – upper right
    const closeBtnW = 44;
    const closeBtnH = 32;
    const closeBtnX = W - pad - closeBtnW;
    const closeBtnY = (headerH - closeBtnH) / 2;

    const closeBg = this.scene.add
      .rectangle(
        closeBtnX + closeBtnW / 2,
        closeBtnY + closeBtnH / 2,
        closeBtnW,
        closeBtnH,
        hexToPhaser(COLORS.RED),
        1,
      )
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });

    const closeText = this.scene.add
      .text(closeBtnX + closeBtnW / 2, closeBtnY + closeBtnH / 2, "✕", {
        fontSize: "18px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
      })
      .setOrigin(0.5)
      .setScrollFactor(0);

    closeBg.on("pointerdown", () => this.hide());

    // Separator
    const sep = this.scene.add
      .rectangle(0, textAreaTop - 1, W, 2, 0x555555)
      .setOrigin(0, 0)
      .setScrollFactor(0);

    // Log content text – always fresh from buffer
    const content =
      logBuffer.length > 0 ? logBuffer.join("\n") : "(no logs yet)";

    this.textObj = this.scene.add
      .text(pad, textAreaTop + pad, content, {
        fontSize: "10px",
        color: "#ccffcc",
        fontFamily: "monospace",
        wordWrap: { width: W - pad * 2, useAdvancedWrap: true },
        lineSpacing: 2,
      })
      .setOrigin(0, 0)
      .setScrollFactor(0);

    // Scroll to bottom so the most recent messages are visible
    const maxScroll = Math.max(0, this.textObj.height - textAreaH + pad);
    this.scrollY = maxScroll;
    this.textObj.setY(textAreaTop + pad - this.scrollY);

    // Geometry mask – the Graphics object lives in the scene (not the container)
    // so its coordinates are in camera/screen space, matching the textObj position.
    this.maskGraphics = this.scene.add.graphics();
    this.maskGraphics.fillStyle(0xffffff);
    this.maskGraphics.fillRect(0, textAreaTop, W, textAreaH);
    this.maskGraphics.setVisible(false); // invisible but still acts as a mask shape
    const mask = this.maskGraphics.createGeometryMask();
    this.textObj.setMask(mask);

    // Invisible drag zone over the text area
    const dragZone = this.scene.add
      .zone(0, textAreaTop, W, textAreaH)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setInteractive();

    dragZone.on("pointerdown", (ptr: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.lastPointerY = ptr.y;
    });

    this.onPointerMove = (ptr: Phaser.Input.Pointer) => {
      if (!this.isDragging || !this.textObj) return;
      const delta = this.lastPointerY - ptr.y;
      this.lastPointerY = ptr.y;
      const max = Math.max(0, this.textObj.height - textAreaH + pad);
      this.scrollY = Phaser.Math.Clamp(this.scrollY + delta, 0, max);
      this.textObj.setY(textAreaTop + pad - this.scrollY);
    };

    this.onPointerUp = () => {
      this.isDragging = false;
    };

    this.scene.input.on("pointermove", this.onPointerMove);
    this.scene.input.on("pointerup", this.onPointerUp);

    // Assemble container (maskGraphics is NOT in container – lives at scene level)
    this.container = this.scene.add.container(0, 0, [
      bg,
      sep,
      title,
      closeBg,
      closeText,
      this.textObj,
      dragZone,
    ]);
    this.container.setDepth(999999);
    this.uiLayer.add(this.container);
  }

  hide() {
    if (this.onPointerMove) {
      this.scene.input.off("pointermove", this.onPointerMove);
      this.onPointerMove = null;
    }
    if (this.onPointerUp) {
      this.scene.input.off("pointerup", this.onPointerUp);
      this.onPointerUp = null;
    }
    // Clear mask before destroying the graphics so Phaser doesn't reference
    // a destroyed object
    if (this.textObj && this.maskGraphics) {
      this.textObj.clearMask();
    }
    if (this.maskGraphics) {
      this.maskGraphics.destroy();
      this.maskGraphics = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
    this.textObj = null;
    this.scrollY = 0;
    this.isDragging = false;
  }

  isVisible() {
    return !!this.container;
  }
}
